import { Divider, SxProps, Theme } from '@mui/material';
import { formatTranslation } from 'intl';
import { ProjectSetupTranslationKeys } from 'intl/keys';
import { useBacktrack } from 'components/common/withBacktrack';
import { composeHOCs } from 'shared/utils/composeHOCs';
import { withProcessesData } from 'components/common/withProcessesData';
import { MemoContractForm } from 'components/contract/Form';
import {
  useCreateContract,
  withCreateContract,
} from 'components/dataCreationForms/contract/withCreateContract';
import { WithInputForm } from 'components/dataCreationForms/withInputForm';
import { useCompanies } from 'store/company';
import { companyModelToLabelledEntity } from 'shared/domain/company/mapping';
import { useFormCleanupOnUnmount } from 'presentation/dialogForms/dialogFormsHooks';
import React, { memo, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { LabelledEntity } from 'shared/types/commonView';
import ViewWrapper from 'components/core/Layout/ViewWrapper';
import { contractSubheaderBaseSx } from 'components/contract/layout';
import { SubheaderText } from 'components/general';
import { SubmitFooter } from 'components/submitFooter';

const subheaderSx: SxProps<Theme> = {
  ...contractSubheaderBaseSx,
  mt: 4,
};

const dividerSx: SxProps<Theme> = {
  my: 4,
};

function CreateContractWizardContainer(): React.ReactElement {
  const {
    submitForm,
    isPosting,
    initialValues,
    releaseSubmitEvent,
    SUBMIT_EVENT_TYPE,
    handleFormValidation,
    validate,
  } = useCreateContract();
  const intl = useIntl();

  const { back } = useBacktrack();
  const formName = 'contract';
  useFormCleanupOnUnmount(formName);
  const onCancel = useCallback(() => {
    back();
  }, [back]);
  const {
    items: companies,
  } = useCompanies();

  const companiesAsLabelledEntities: LabelledEntity[] = useMemo(
    () => companies.map(companyModelToLabelledEntity),
    [companies]
  );

  return (
    <>
      <ViewWrapper>
        <WithInputForm
          values={initialValues}
          errors={{}}
          handleFormValidation={handleFormValidation}
          handleFieldValidation={validate}
          onSubmit={submitForm}
          eventType={SUBMIT_EVENT_TYPE}
          dispatchSubmit={releaseSubmitEvent}
          formName={formName}
        >
          <SubheaderText sx={subheaderSx}>
            {formatTranslation(intl, ProjectSetupTranslationKeys.addContractSection)}
          </SubheaderText>

          <Divider sx={dividerSx} />

          <MemoContractForm
            companies={companiesAsLabelledEntities}
            isDialog={false}
          />
        </WithInputForm>
      </ViewWrapper>

      <SubmitFooter
        isPosting={isPosting}
        onCancel={onCancel}
        onSubmit={releaseSubmitEvent}
      />
    </>
  );
}

export const MemoCreateContractWizardContainer = memo(
  composeHOCs(withProcessesData, withCreateContract)(
    CreateContractWizardContainer
  )
);
